import { FeedbackBanner } from "@/core/components/feedback-banner";
import { Button } from "@/core/components/ui/button";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import type { PayrollStatementNotificationRecipient } from "./types";
import { getChangedPacksSummary } from "./utils";

/**
 * The packs already emailed that no longer describe anybody's pay.
 *
 * A rerun or a single edited cell can move pay that has already gone out, and until
 * this banner the only way to find out whose was to open every row and compare it
 * against a figure nobody had kept. So managers either resent the whole period —
 * emailing everybody a pack identical to the one they already had — or resent nobody
 * and left technicians reading a number that was no longer their pay.
 *
 * Selects rather than sends. It hands the existing Send flow exactly this set, so the
 * confirmation step, the note drafts, and the failure reporting are all the ones the
 * manager already knows, and nothing goes out without the same click as always.
 */
export const ChangedPacksBanner = ({
  changedRecipients,
  blockedRecipients = [],
  onSelect,
  disabled,
}: {
  changedRecipients: PayrollStatementNotificationRecipient[];
  /**
   * Changed packs that cannot be sent — no saved address, or a pack that no longer
   * projects. Named rather than dropped: their rows are marked like any other changed
   * pack, so leaving them out of both the count and the copy left a mark on the page
   * that nothing accounted for.
   */
  blockedRecipients?: PayrollStatementNotificationRecipient[];
  onSelect: () => void;
  /** Off the email rollout there is nothing to resend and no button to press. */
  disabled?: boolean;
}) => {
  const count = changedRecipients.length;
  const { headline, detail } = getChangedPacksSummary({
    changedRecipients,
    blockedRecipients,
  });
  if (!headline) return null;

  return (
    <FeedbackBanner variant="warning" className="w-full">
      <Stack
        direction="row"
        align="center"
        justify="between"
        gap="md"
        fullWidth
        className="min-w-0 flex-wrap gap-y-2"
      >
        <Stack gap="xs" align="stretch" className="min-w-0">
          <Typography variant="bodyW3" className="font-semibold">
            {headline}
          </Typography>
          {detail && (
            <Typography variant="captionW1" textColor="secondary">
              {detail}
            </Typography>
          )}
        </Stack>
        {!disabled && count > 0 && (
          // ml-auto so the button stays on the right when a long list of names
          // wraps it onto its own line; justify-between alone drops it to the left.
          <Button
            size="sm"
            variant="secondary"
            className="ml-auto shrink-0"
            onClick={onSelect}
          >
            {count === 1 ? "Select them" : `Select these ${count}`}
          </Button>
        )}
      </Stack>
    </FeedbackBanner>
  );
};
